import React from "react";
import {
  Download,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
} from "lucide-react";
import { TreasuryEntry, TreasurySummary } from "../../../../services/treasuryReportingService";

interface Props {
  entries: TreasuryEntry[];
  totalEntries: number;
  summary: TreasurySummary | null;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onEdit: (entry: TreasuryEntry) => void;
  onDelete: (id: string) => void;
  onDownload: () => void;
}

const formatNum = (n: number) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TreasuryTab: React.FC<Props> = ({
  entries,
  totalEntries,
  summary,
  page,
  pageSize,
  loading,
  onPageChange,
  onEdit,
  onDelete,
  onDownload,
}) => {
  const totalPages = Math.ceil(totalEntries / pageSize);

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <PiggyBank size={16} />
              <span>Capital total</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{formatNum(summary.total_capital_start)}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <TrendingUp size={16} className="text-green-600" />
              <span>Revenu total</span>
            </div>
            <div className="text-xl font-bold text-green-600">{formatNum(summary.total_daily_revenue)}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <TrendingDown size={16} className="text-red-600" />
              <span>Dépenses total</span>
            </div>
            <div className="text-xl font-bold text-red-600">{formatNum(summary.total_daily_expenses)}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Wallet size={16} />
              <span>Stock financier</span>
            </div>
            <div className="text-xl font-bold text-blue-600">{formatNum(summary.total_financial_stock)}</div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={onDownload}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white rounded-lg border hover:bg-gray-50"
        >
          <Download size={16} />
          Exporter Excel
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Date</th>
              <th className="px-3 py-2 text-right font-medium">Capital départ</th>
              <th className="px-3 py-2 text-right font-medium">Volume</th>
              <th className="px-3 py-2 text-right font-medium">Revenu</th>
              <th className="px-3 py-2 text-right font-medium">Dépenses</th>
              <th className="px-3 py-2 text-right font-medium">Résultat</th>
              <th className="px-3 py-2 text-right font-medium">Bank</th>
              <th className="px-3 py-2 text-right font-medium">MTN</th>
              <th className="px-3 py-2 text-right font-medium">Crypto</th>
              <th className="px-3 py-2 text-right font-medium">Stock</th>
              <th className="px-3 py-2 text-right font-medium">Écart</th>
              <th className="px-3 py-2 text-left font-medium">Commentaire</th>
              <th className="px-3 py-2 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-gray-400">
                  Chargement...
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-gray-400">
                  Aucune entrée
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">{entry.report_date}</td>
                  <td className="px-3 py-2 text-right">{formatNum(entry.capital_start)}</td>
                  <td className="px-3 py-2 text-right">{formatNum(entry.daily_volume)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatNum(entry.daily_revenue)}</td>
                  <td className="px-3 py-2 text-right text-red-600">{formatNum(entry.daily_expenses)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatNum(entry.result)}</td>
                  <td className="px-3 py-2 text-right">{formatNum(entry.bank_balance)}</td>
                  <td className="px-3 py-2 text-right">{formatNum(entry.mtn_balance)}</td>
                  <td className="px-3 py-2 text-right">{formatNum(entry.crypto_available)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatNum(entry.financial_stock)}</td>
                  <td
                    className={`px-3 py-2 text-right font-medium ${entry.gap >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {formatNum(entry.gap)}
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate" title={entry.comment}>
                    {entry.comment}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onEdit(entry)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(entry.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {page + 1} / {totalPages} · {totalEntries} entrées
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasuryTab;
